import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import ResizeObserverPolyfill from "resize-observer-polyfill";

const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;


const checkOverflow = ref => !!ref.current &&
      (ref.current.scrollHeight > ref.current.offsetHeight ||
        ref.current.scrollWidth > ref.current.offsetWidth);

const useFitText = ({
  maxFontSize = 100,
  minFontSize = 20,
  resolution = 5,
} = {}) => {
  const initState = useCallback(
    () => ({
      fontSize: maxFontSize,
      fontSizePrev: minFontSize,
      fontSizeMax: maxFontSize,
      fontSizeMin: minFontSize,
    }),
    [maxFontSize, minFontSize],
  );

  const ref = useRef(null);
  const [state, setState] = useState(initState);
  const { fontSize, fontSizeMax, fontSizeMin, fontSizePrev } = state;
  // montior div size changes and recalculate on resize
  const [ro] = useState(() => {
     return new ResizeObserver(()=>{
      setState(initState());
      setTimeout(()=>{ //recheck after 500ms because setting innerHTML doesn't stay in sync with virtual dom
        if (checkOverflow(ref)) setState(initState());
      },500)
     })
  });

  useEffect(() => {
    if (ref.current) {
      ro.observe(ref.current);
    }
    return () => {
      ro.disconnect();
    };
  }, [ro]);

  // check overflow and resize font
  useLayoutEffect(() => {
    const isDone = Math.abs(fontSize - fontSizePrev) <= resolution;
    const isOverflow = checkOverflow(ref);
    const isAsc = fontSize > fontSizePrev;

    // return if the font size has been adjusted "enough" (change within `resolution`)
    // reduce font size by one increment if it's overflowing
    if (isDone) {
      if (isOverflow) {
        const fontSizeNew =
          fontSizePrev < fontSize
            ? fontSizePrev
            : fontSize - (fontSizePrev - fontSize);
        setState({
          fontSize: fontSizeNew,
          fontSizeMax,
          fontSizeMin,
          fontSizePrev,
        });
      }
      return;
    }

    // binary search to adjust font size
    let delta;
    let newMax = fontSizeMax;
    let newMin = fontSizeMin;
    if (isOverflow) {
      delta = isAsc ? fontSizePrev - fontSize : fontSizeMin - fontSize;
      newMax = Math.min(fontSizeMax, fontSize);
    } else {
      delta = isAsc ? fontSizeMax - fontSize : fontSizePrev - fontSize;
      newMin = Math.max(fontSizeMin, fontSize);
    }
    setState({
      fontSize: fontSize + delta / 2,
      fontSizeMax: newMax,
      fontSizeMin: newMin,
      fontSizePrev: fontSize,
    });
  }, [fontSize, fontSizeMax, fontSizeMin, fontSizePrev, ref, resolution]);

  return { fontSize: `${fontSize}%`, ref };
};

export default useFitText;
